import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useAuth } from "../../lib/auth";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  UserPlus, ShieldCheck, ShieldX, Send, X as XIcon, Copy, RefreshCw,
  Clock, MailCheck, MailX, Shield, KeyRound,
} from "lucide-react";

const ROLE_OPTS = [
  { value: "admin",      label: "Admin",      hint: "Full back-office access incl. team management" },
  { value: "treasury",   label: "Treasury",   hint: "Approve fund movements, refunds, wallet whitelists" },
  { value: "compliance", label: "Compliance", hint: "Review KYC / SoF / risk flags" },
  { value: "support",    label: "Support",    hint: "Customer 360 read-only, no fund actions" },
];

const ROLE_TONE = {
  admin:      "bg-indigo-50 text-indigo-700 border-indigo-200",
  treasury:   "bg-emerald-50 text-emerald-700 border-emerald-200",
  compliance: "bg-amber-50 text-amber-800 border-amber-200",
  support:    "bg-slate-100 text-slate-700 border-slate-200",
};

const INVITE_STATUS_TONE = {
  pending:   "bg-amber-50 text-amber-800 border-amber-200",
  redeemed:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

function _fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}


export default function AdminTeam() {
  const { user: me } = useAuth();
  const amAdmin = (me?.role || "admin") === "admin";
  const [staff, setStaff] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Invite dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [iEmail, setIEmail] = useState("");
  const [iName, setIName] = useState("");
  const [iRole, setIRole] = useState("compliance");
  const [iBusy, setIBusy] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState(null);

  // Role-change inline state
  const [editingRoleFor, setEditingRoleFor] = useState(null);
  const [editingRoleTo, setEditingRoleTo] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/team/staff"),
        api.get("/admin/team/invites"),
      ]);
      setStaff(a.data?.staff || []);
      setInvites(b.data?.invites || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load team");
    } finally { setLoading(false); }
  };
  /* eslint-disable */
  useEffect(() => { load(); }, []);
  /* eslint-enable */

  const submitInvite = async () => {
    if (!iEmail.trim()) { toast.error("Email is required"); return; }
    setIBusy(true);
    try {
      const r = await api.post("/admin/team/invites", {
        email: iEmail.trim(), role: iRole, name: iName.trim() || undefined,
      });
      setLastInviteUrl(r.data?.invite_url_one_time || null);
      toast.success(`Invitation sent to ${iEmail.trim()}`);
      setIEmail(""); setIName(""); setIRole("compliance");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create invitation");
    } finally { setIBusy(false); }
  };

  const cancelInvite = async (inv) => {
    if (!window.confirm(`Cancel invitation to ${inv.email}?`)) return;
    try {
      await api.delete(`/admin/team/invites/${inv.id}`);
      toast.success("Invitation cancelled");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const resendInvite = async (inv) => {
    try {
      const r = await api.post(`/admin/team/invites/${inv.id}/resend`);
      setLastInviteUrl(r.data?.invite_url_one_time || null);
      toast.success(`Invitation re-sent to ${inv.email}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveRoleChange = async (u) => {
    if (!editingRoleTo || editingRoleTo === u.role) {
      setEditingRoleFor(null);
      return;
    }
    try {
      await api.patch(`/admin/team/staff/${u.id}`, { role: editingRoleTo });
      toast.success(`Role updated to ${editingRoleTo}`);
      setEditingRoleFor(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const revokeStaff = async (u) => {
    const reason = window.prompt(`Revoke staff access for ${u.email}?\n\nOptional reason (for audit log):`);
    if (reason === null) return;  // cancelled
    try {
      await api.post(`/admin/team/staff/${u.id}/revoke`, { reason: reason || undefined });
      toast.success(`${u.email} revoked`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const copyInviteUrl = async () => {
    if (!lastInviteUrl) return;
    try {
      await navigator.clipboard.writeText(lastInviteUrl);
      toast.success("Invite URL copied — you can share it manually if needed");
    } catch { toast.error("Copy failed — select the URL and copy manually"); }
  };

  return (
    <AdminLayout
      title="Team"
      subtitle="Invite back-office staff. New members are forced to enrol 2FA on first sign-in."
      actions={amAdmin && (
        <Button onClick={() => setInviteOpen(true)} size="sm" className="rounded-xl gap-1.5"
                data-testid="team-invite-open-btn">
          <UserPlus className="w-3.5 h-3.5" /> Invite staff
        </Button>
      )}
    >
      {!amAdmin && (
        <div className="xch-card p-4 mb-4 bg-amber-50 border-amber-200" data-testid="team-readonly-banner">
          <div className="flex items-start gap-2.5">
            <Shield className="w-4 h-4 mt-0.5 text-amber-700" />
            <div className="text-xs text-amber-900">
              <div className="font-semibold">Read-only view</div>
              <div>Only the <strong>admin</strong> role can invite, change roles, or revoke staff. Ask an admin to make changes for you.</div>
            </div>
          </div>
        </div>
      )}

      {lastInviteUrl && amAdmin && (
        <div className="xch-card p-3 mb-4 bg-emerald-50 border-emerald-200" data-testid="team-last-invite-url">
          <div className="flex items-start gap-2.5 flex-wrap">
            <MailCheck className="w-4 h-4 mt-0.5 text-emerald-700 shrink-0" />
            <div className="text-xs text-emerald-900 flex-1 min-w-[200px]">
              <div className="font-semibold mb-0.5">Invitation sent.</div>
              <div className="mb-1">The recipient will get an email with a one-time link. Need to share it manually? Copy below:</div>
              <code className="block bg-white border border-emerald-200 rounded-md px-2 py-1 text-[10px] break-all">{lastInviteUrl}</code>
            </div>
            <div className="flex gap-1.5">
              <Button onClick={copyInviteUrl} variant="outline" size="sm" className="rounded-xl gap-1.5 h-8"
                      data-testid="team-copy-invite-btn">
                <Copy className="w-3.5 h-3.5" /> Copy
              </Button>
              <Button onClick={() => setLastInviteUrl(null)} variant="ghost" size="sm" className="rounded-xl h-8 w-8 p-0">
                <XIcon className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Active staff */}
      <div className="xch-card p-0 mb-5 overflow-hidden" data-testid="team-staff-table">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="font-display font-bold">Active staff ({staff.filter(s => s.is_staff).length})</div>
          <Button onClick={load} variant="ghost" size="sm" className="rounded-xl h-7 gap-1.5"
                  data-testid="team-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
        </div>
        <div className="divide-y divide-border">
          {loading && <div className="p-4 text-xs text-muted-foreground">Loading…</div>}
          {!loading && staff.length === 0 && (
            <div className="p-4 text-xs text-muted-foreground">No staff members yet. Invite one above.</div>
          )}
          {staff.filter(s => s.is_staff).map((u) => {
            const tone = ROLE_TONE[u.role] || ROLE_TONE.admin;
            const isMe = u.id === me?.id;
            const editing = editingRoleFor === u.id;
            return (
              <div key={u.id} className="px-4 py-3 flex items-center gap-3 flex-wrap"
                   data-testid={`team-staff-row-${u.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">
                    {u.name || u.email}
                    {isMe && <span className="ml-2 text-[10px] text-muted-foreground">(you)</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">{u.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  {u.totp_enabled
                    ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] gap-1">
                        <KeyRound className="w-3 h-3" /> 2FA on
                      </Badge>
                    : <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px] gap-1">
                        <KeyRound className="w-3 h-3" /> 2FA missing
                      </Badge>}
                  {editing ? (
                    <>
                      <Select value={editingRoleTo || u.role} onValueChange={setEditingRoleTo}>
                        <SelectTrigger className="rounded-xl h-8 text-xs w-[140px]" data-testid={`team-role-select-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button onClick={() => saveRoleChange(u)} size="sm" className="rounded-xl h-8 gap-1.5"
                              data-testid={`team-role-save-${u.id}`}>Save</Button>
                      <Button onClick={() => setEditingRoleFor(null)} variant="ghost" size="sm" className="rounded-xl h-8">Cancel</Button>
                    </>
                  ) : (
                    <Badge variant="outline" className={`${tone} text-[10px] uppercase tracking-wider font-bold`}
                           data-testid={`team-role-badge-${u.id}`}>
                      {u.role || "admin"}
                    </Badge>
                  )}
                  {amAdmin && !editing && !isMe && (
                    <>
                      <Button onClick={() => { setEditingRoleFor(u.id); setEditingRoleTo(u.role); }}
                              variant="ghost" size="sm" className="rounded-xl h-8 gap-1.5"
                              data-testid={`team-role-edit-${u.id}`}>
                        Change role
                      </Button>
                      <Button onClick={() => revokeStaff(u)} variant="outline" size="sm"
                              className="rounded-xl h-8 gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50"
                              data-testid={`team-revoke-${u.id}`}>
                        <ShieldX className="w-3.5 h-3.5" /> Revoke
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pending / recent invitations */}
      <div className="xch-card p-0 overflow-hidden" data-testid="team-invites-table">
        <div className="px-4 py-3 border-b border-border">
          <div className="font-display font-bold">Invitations</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">Pending invitations expire after 7 days. Cancelled / redeemed ones stay here for audit.</div>
        </div>
        <div className="divide-y divide-border">
          {loading && <div className="p-4 text-xs text-muted-foreground">Loading…</div>}
          {!loading && invites.length === 0 && (
            <div className="p-4 text-xs text-muted-foreground">No invitations yet.</div>
          )}
          {invites.map((inv) => {
            const tone = INVITE_STATUS_TONE[inv.status] || INVITE_STATUS_TONE.cancelled;
            const isPending = inv.status === "pending";
            return (
              <div key={inv.id} className="px-4 py-3 flex items-center gap-3 flex-wrap"
                   data-testid={`team-invite-row-${inv.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{inv.email}</div>
                  <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1.5">
                    <Clock className="w-3 h-3" />
                    Invited {_fmtDate(inv.created_at)} by {inv.invited_by_email || "—"}
                    {isPending && <> · expires {_fmtDate(inv.expires_at)}</>}
                    {inv.status === "redeemed" && inv.redeemed_at && <> · redeemed {_fmtDate(inv.redeemed_at)}</>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`${ROLE_TONE[inv.role]} text-[10px] uppercase tracking-wider font-bold`}>
                    {inv.role}
                  </Badge>
                  <Badge variant="outline" className={`${tone} text-[10px] uppercase tracking-wider font-bold`}>
                    {inv.status}
                  </Badge>
                  {amAdmin && isPending && (
                    <>
                      <Button onClick={() => resendInvite(inv)} variant="outline" size="sm" className="rounded-xl h-8 gap-1.5"
                              data-testid={`team-invite-resend-${inv.id}`}>
                        <Send className="w-3.5 h-3.5" /> Resend
                      </Button>
                      <Button onClick={() => cancelInvite(inv)} variant="outline" size="sm"
                              className="rounded-xl h-8 gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50"
                              data-testid={`team-invite-cancel-${inv.id}`}>
                        <XIcon className="w-3.5 h-3.5" /> Cancel
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md" data-testid="team-invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite a team member</DialogTitle>
            <DialogDescription>
              They&apos;ll receive an email with a one-time link to set their password.
              2FA enrolment is mandatory on first sign-in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="team-invite-email" className="text-xs">Email</Label>
              <Input id="team-invite-email" type="email" value={iEmail}
                     onChange={(e) => setIEmail(e.target.value)}
                     placeholder="colleague@xchangely.io"
                     data-testid="team-invite-email-input" />
            </div>
            <div>
              <Label htmlFor="team-invite-name" className="text-xs">Name (optional)</Label>
              <Input id="team-invite-name" value={iName}
                     onChange={(e) => setIName(e.target.value)}
                     placeholder="Alex Compliance"
                     data-testid="team-invite-name-input" />
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={iRole} onValueChange={setIRole}>
                <SelectTrigger className="rounded-xl" data-testid="team-invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTS.map(r => (
                    <SelectItem key={r.value} value={r.value}>
                      <div className="flex flex-col">
                        <span>{r.label}</span>
                        <span className="text-[10px] text-muted-foreground">{r.hint}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInviteOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={submitInvite} disabled={iBusy || !iEmail.trim()}
                    className="rounded-xl gap-1.5"
                    data-testid="team-invite-submit-btn">
              <Send className="w-4 h-4" />
              {iBusy ? "Sending…" : "Send invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
